import { z } from "zod";
import {
  EDiscountType,
  EPromotionStatus,
  EPromotionType,
  EUsageLimitType,
} from "../types/Promotion";

// Common schema for independent fields
const basePromotionSchema = {
  code: z.string().min(1, { message: "Promo code is required" }),
  description: z.string().optional(),
  status: z.nativeEnum(EPromotionStatus).default(EPromotionStatus.ACTIVE),
  startDate: z.coerce.date({
    errorMap: () => ({ message: "Start date is required" }),
  }),
  endDate: z.coerce.date({
    errorMap: () => ({ message: "End date is required" }),
  }),
  // numberOfVoucher: z.number().finite().nonnegative().min(1, {
  //   message: "Please enter voucher number",
  // }),
  usageLimitType: z.nativeEnum(EUsageLimitType).default(EUsageLimitType.PER_PROMO_CODE),
  usageLimit: z.number().finite().positive({ message: "Usage limit must be greater than 0" }),
  type: z.nativeEnum(EPromotionType).default(EPromotionType.PACKAGE),
};

// Base schema for updating promotion - without type and numberOfVoucher
const baseUpdatePromotionSchema = {
  code: z.string().min(1, { message: "Promo code is required" }),
  description: z.string().optional(),
  status: z.nativeEnum(EPromotionStatus).default(EPromotionStatus.ACTIVE),
  startDate: z.coerce.date({
    errorMap: () => ({ message: "Start date is required" }),
  }),
  endDate: z.coerce.date({
    errorMap: () => ({ message: "End date is required" }),
  }),
  usageLimitType: z
    .nativeEnum(EUsageLimitType)
    .default(EUsageLimitType.PER_PROMO_CODE),
  usageLimit: z
    .number()
    .finite()
    .positive({ message: "Usage limit must be greater than 0" }),
};

// Specific schema for PERCENT discount type
const percentDiscountSchema = z.object({
  ...basePromotionSchema,
  discountType: z.literal(EDiscountType.PERCENT),
  discount: z
    .number()
    .finite()
    .positive({ message: "Discount must be greater than 0" })
    .max(100, { message: "Percentage discount cannot exceed 100%" }),
});

// Specific schema for PRICE discount type
const priceDiscountSchema = z.object({
  ...basePromotionSchema,
  discountType: z.literal(EDiscountType.PRICE),
  discount: z
    .number()
    .finite()
    .positive({ message: "Discount must be greater than 0" }),
});

// Specific schema for PERCENT discount type (update version)
const updatePercentDiscountSchema = z.object({
  ...baseUpdatePromotionSchema,
  discountType: z.literal(EDiscountType.PERCENT),
  discount: z
    .number()
    .finite()
    .positive({ message: "Discount must be greater than 0" })
    .max(100, { message: "Percentage discount cannot exceed 100%" }),
});

// Specific schema for PRICE discount type (update version)
const updatePriceDiscountSchema = z.object({
  ...baseUpdatePromotionSchema,
  discountType: z.literal(EDiscountType.PRICE),
  discount: z
    .number()
    .finite()
    .positive({ message: "Discount must be greater than 0" }),
});

// Date validation refinement
const dateRefinement = (schema: z.ZodType) =>
  schema.refine(
    (data) => {
      // If both dates have values, start date must be <= end date
      if (data.startDate && data.endDate) {
        return data.startDate <= data.endDate;
      }
      return true; // Pass validation if one of the fields is empty
    },
    {
      message: "End date must be on or after start date",
      path: ["endDate"], // Apply error only to the endDate field
    },
  );

// Combine schemas with discriminated union for creation
export const createPromotionSchema = dateRefinement(
  z.discriminatedUnion("discountType", [
    percentDiscountSchema,
    priceDiscountSchema,
  ]),
);

// Combine schemas with discriminated union for updating
export const updatePromotionSchema = dateRefinement(
  z.discriminatedUnion("discountType", [
    updatePercentDiscountSchema,
    updatePriceDiscountSchema,
  ]),
);
